import { Content } from "pdfmake/interfaces";
import { LegendItem, Polygon as PolygonStyle, Raster } from "helpers/models";
import layout from "report/common/layout";
import getLayer from "report/common/layer";
import getRect from "report/common/rect";
import getColor from "report/common/color";
import getOutside from "report/common/outside";
import svg from "report/common/svg";
import getMatchingTitle from "components/fathomAnalyze/util";
import gap from "report/common/gap";
import { PolygonLayerStatistic } from "../types";

const exclude = ["No Data", "No_Data", "Outside", "No Observation"];
const grey = ["No Data", "No_Data", "Outside", "No Observation", "Waterbody"];
const coordinateHiddenRiskLevels = new Set([
  "Negligible",
  "Outside",
  "No Observation",
  "Waterbody",
  "No Data",
  "No_Data"
]);
const minReturnPeriodLayerIds = new Set([
  "FL_Fluvial_SwissRe",
  "FL_Pluvial_SwissRe",
  "FL_Surge_SwissRe"
]);
const pageSummaryAlignedLayerIds = new Set([
  "FL_Fluvial_SwissRe",
  "FL_Pluvial_SwissRe",
  "FL_Surge_SwissRe"
]);

const resolveDetailMetrics = (metrics: PolygonLayerStatistic["metrics"]) => {
  if (!metrics) {
    return undefined;
  }

  const bundle = metrics as any;
  const hasBundledMetrics = !!bundle.intensityMetrics || !!bundle.detailMetrics;

  if (!hasBundledMetrics) {
    return metrics;
  }

  return bundle.detailMetrics || bundle.intensityMetrics;
};

const resolveSummaryMetrics = (metrics: PolygonLayerStatistic["metrics"]) => {
  if (!metrics) {
    return undefined;
  }

  const bundle = metrics as any;
  const hasBundledMetrics = !!bundle.intensityMetrics || !!bundle.detailMetrics;

  if (!hasBundledMetrics) {
    return metrics;
  }

  return bundle.intensityMetrics || bundle.detailMetrics;
};

const normalizeRiskLevelForLegend = (riskLevel: string, layerId: string) => {
  let sortIndex: number | undefined;
  let normalizedRiskLevel = riskLevel;

  if (layerId.includes("Fathom")) {
    if (["5 years", "10 years", "20 years", "50 years"].includes(riskLevel)) {
      if (riskLevel === "5 years") {
        sortIndex = 9;
      } else if (riskLevel === "10 years") {
        sortIndex = 8;
      } else if (riskLevel === "20 years") {
        sortIndex = 7;
      }
      normalizedRiskLevel = "≤50 years";
    } else if (riskLevel === "1000 years") {
      normalizedRiskLevel = "≥1000 years";
    }
  } else if (layerId === "FL_Fluvial_Official" && riskLevel === "> 500 years") {
    normalizedRiskLevel = "> 500 years (US)";
  } else if (
    riskLevel === "Negligible (< 0.014)" ||
    riskLevel === "Negligible"
  ) {
    sortIndex = 0.5;
  }

  return {
    normalizedRiskLevel,
    sortIndex
  };
};

const sortRiskDistributionByLegend = (
  riskDistribution: PolygonLayerStatistic["riskDistribution"] | undefined,
  legendInfo: LegendItem | undefined
) => {
  const list = riskDistribution || [];
  if (!list.length) {
    return list;
  }

  const rules = legendInfo?.rules || [];
  if (!rules.length) {
    return list;
  }

  const levelOrder = new Map<string, number>();
  const layerId = String(legendInfo?.layerId || "");
  rules.forEach((rule: any, index: number) => {
    const title = String(rule?.title || "").trim();
    if (title) {
      levelOrder.set(title, index);
    }
  });

  const getRiskSortIndex = (riskLevel: string) => {
    const normalizedRiskLevel = String(riskLevel || "").trim();

    if (
      normalizedRiskLevel === "" ||
      normalizedRiskLevel === "No Data" ||
      normalizedRiskLevel === "No_Data" ||
      normalizedRiskLevel === "Outside" ||
      normalizedRiskLevel === "No Observation"
    ) {
      return -1;
    }

    const normalized = normalizeRiskLevelForLegend(
      normalizedRiskLevel,
      layerId
    );
    return (
      normalized.sortIndex ??
      levelOrder.get(normalized.normalizedRiskLevel) ??
      levelOrder.get(normalizedRiskLevel) ??
      0
    );
  };

  return [...list].sort((a, b) => {
    const aSortIndex = getRiskSortIndex(String(a?.riskLevel || ""));
    const bSortIndex = getRiskSortIndex(String(b?.riskLevel || ""));
    return bSortIndex - aSortIndex;
  });
};

const getTranslatedRiskLevel = (t: any, riskLevel: string) => {
  const key = `legend.${riskLevel}`;
  const translated = String(t(key));
  return translated === key ? riskLevel : translated;
};

const getRiskLevelLegendColor = (
  riskLevel: string,
  legendInfo: LegendItem | undefined
) => {
  const normalizedRiskLevel = String(riskLevel || "").trim();

  if (
    normalizedRiskLevel === "Outside" ||
    normalizedRiskLevel === "No Observation"
  ) {
    return {
      color: "#f2f2f2",
      opacity: 0.5
    };
  }

  if (
    normalizedRiskLevel === "" ||
    normalizedRiskLevel === "No Data" ||
    normalizedRiskLevel === "No_Data"
  ) {
    return {
      color: "grey",
      opacity: 0.5
    };
  }

  if (normalizedRiskLevel === "Waterbody") {
    return {
      color: "#DCDBDC",
      opacity: 0.5
    };
  }

  const rules = legendInfo?.rules || [];
  if (!rules.length) {
    return {
      color: "#cbd5e1",
      opacity: 1
    };
  }

  const layerId = String(legendInfo?.layerId || "");
  const normalized = normalizeRiskLevelForLegend(normalizedRiskLevel, layerId);
  const targetRule =
    rules.find((rule: any) => rule?.title === normalized.normalizedRiskLevel) ||
    rules.find((rule: any) => rule?.title === normalizedRiskLevel);

  const symbol = targetRule?.symbolizers?.[0];
  return {
    color:
      (symbol as Raster | undefined)?.raster?.color ??
      (symbol as PolygonStyle | undefined)?.polygon?.fill ??
      "#cbd5e1",
    opacity:
      (symbol as Raster | undefined)?.raster?.opacity ??
      (symbol as PolygonStyle | undefined)?.polygon?.fillOpacity ??
      1
  };
};

const getRiskLevelLegendSquare = (
  riskLevel: string,
  legendInfo?: LegendItem
) => {
  const swatchStyle = getRiskLevelLegendColor(riskLevel, legendInfo);
  return {
    canvas: [
      {
        type: "rect" as const,
        x: 0,
        y: 1,
        w: 8,
        h: 8,
        lineColor: "#E4E4E4",
        lineWidth: 1,
        color: swatchStyle.color,
        opacity: swatchStyle.opacity
      }
    ]
  };
};

const toDisplayNumber = (value: number, digits: number) => {
  return String(parseFloat(value.toFixed(digits)));
};

const toFiniteNumber = (value: unknown, fallback = 0) => {
  const num = Number(value);
  return Number.isFinite(num) ? num : fallback;
};

const formatFixedNumber = (value: unknown, digits: number, fallback = "-") => {
  const num = Number(value);
  if (!Number.isFinite(num)) {
    return fallback;
  }

  return num.toFixed(digits);
};

const formatMetricNumber = (value?: number) => {
  const safeValue = toFiniteNumber(value, 0);
  return toDisplayNumber(safeValue, 4);
};

const formatMetricNumberLikePage = (value: unknown) => {
  let numericValue: number | undefined;

  if (typeof value === "number" && Number.isFinite(value)) {
    numericValue = value;
  } else if (typeof value === "string") {
    const trimmed = value.trim();
    if (trimmed !== "") {
      const parsed = Number(trimmed);
      if (Number.isFinite(parsed)) {
        numericValue = parsed;
      }
    }
  }

  if (numericValue === undefined) {
    return undefined;
  }

  return String(parseFloat(numericValue.toFixed(2)));
};

const getMetricDisplayValueLikePage = (t: any, value: unknown) => {
  const formatted = formatMetricNumberLikePage(value);
  if (formatted !== undefined) {
    return formatted;
  }

  return t(`legend.${value}`);
};

/**
 * 生成单个灾害的详细分析块
 * 样式对齐单点分析明细：getLayer 标题 + 颜色条 + 各项指标表格
 */
const getHazardDetail = (
  t: any,
  layerStatistic: PolygonLayerStatistic,
  legendInfo?: LegendItem,
  isLast: boolean = false
): Content => {
  const summaryMetrics = resolveSummaryMetrics(layerStatistic.metrics);
  const metrics = resolveDetailMetrics(layerStatistic.metrics);
  const p90Label =
    summaryMetrics?.p90ValueLabel || metrics?.p90ValueLabel || "";
  const isGreyLegendBar = grey.includes(String(p90Label || "").trim());
  const maxValuePoints = layerStatistic.maxValuePoints || [];
  const maxPoint = maxValuePoints[0];
  const extremeValueLabel = minReturnPeriodLayerIds.has(layerStatistic.layerId)
    ? t(`drawShape.polygonResultMax.${layerStatistic.layerId}`)
    : t("report.Maximum Value");
  const shouldAlignExtremeValueWithPage = minReturnPeriodLayerIds.has(
    layerStatistic.layerId
  );
  const shouldAlignSummaryWithPage = pageSummaryAlignedLayerIds.has(
    layerStatistic.layerId
  );
  const sortedRiskDistribution = sortRiskDistributionByLegend(
    layerStatistic.riskDistribution,
    legendInfo
  );
  const shouldHideCoordinateRow =
    sortedRiskDistribution.length === 1 &&
    coordinateHiddenRiskLevels.has(
      String(sortedRiskDistribution[0]?.riskLevel || "").trim()
    );

  const content: Content[] = [];

  // 1. 灾害图标 + 名称（对齐单点明细样式）
  content.push(getLayer(t, layerStatistic.layerId, 10, 1));
  content.push(gap(10));
  content.push({
    text: t("report.P90 Classification"),
    fontSize: 8,
    opacity: 0.6,
    margin: [0, 0, 0, 6]
  });

  // 2. 颜色条（对齐单点明细样式）
  if (legendInfo?.rules?.length) {
    const rules = legendInfo.rules.filter((r) => !exclude.includes(r.title));
    const index = rules.findIndex(
      (item) =>
        item.title === p90Label ||
        item.title ===
          getMatchingTitle(p90Label.replace("years", "").replace(" ", ""))
    );
    const colorArray = getColor(rules);
    const fillColor = p90Label === "Waterbody" ? "#DCDBDC" : "#D3D3D3";
    content.push(
      isGreyLegendBar
        ? getOutside(fillColor, 555)
        : getRect(colorArray, index, 555)
    );
    content.push(gap(10));
  }

  // 3. 分析概览小节
  // const overviewBody: any[][] = [];

  // // P90 风险等级
  // if (metrics?.p90ValueLabel) {
  //   overviewBody.push([
  //     { text: t("report.P90 Classification"), opacity: 0.6 },
  //     { text: String(metrics.p90ValueLabel) }
  //   ]);
  // }

  // // P90 强度值
  // if (metrics?.p90 !== undefined) {
  //   overviewBody.push([
  //     { text: t("report.P90 Intensity"), opacity: 0.6 },
  //     { text: String(metrics.p90.toFixed(2)) }
  //   ]);
  // }

  // if (overviewBody.length > 0) {
  //   content.push({
  //     text: t("report.Analysis Overview"),
  //     fontSize: 9,
  //     bold: true,
  //     margin: [0, 10, 0, 5]
  //   });
  //   content.push({
  //     table: {
  //       headerRows: 0,
  //       widths: ["*", "*"],
  //       body: overviewBody
  //     },
  //     layout: layout(),
  //     fontSize: 8,
  //     margin: [0, 0, 0, 10]
  //   });
  // }

  // 4. 极值及地点小节
  if (metrics?.max !== undefined || maxPoint) {
    const extremeBody: any[][] = [];

    extremeBody.push([
      { text: extremeValueLabel, opacity: 0.6 },
      {
        text: shouldAlignExtremeValueWithPage
          ? getMetricDisplayValueLikePage(t, metrics?.max)
          : metrics?.max !== undefined
            ? formatFixedNumber(metrics.max, 2)
            : maxPoint
              ? formatFixedNumber(maxPoint.value, 2)
              : "-"
      }
    ]);

    if (!shouldHideCoordinateRow) {
      extremeBody.push([
        { text: t("report.Coordinate"), opacity: 0.6 },
        maxValuePoints.length > 1
          ? {
              columnGap: 4,
              columns: [
                {
                  text: maxPoint
                    ? `${maxPoint.longitude}, ${maxPoint.latitude}`
                    : "-",
                  width: "auto"
                },
                {
                  svg: svg.lineExtremeMultipleLocations,
                  fit: [10, 10] as [number, number],
                  width: 10,
                  margin: [0, 0, 4, 0] as [number, number, number, number]
                },
                {
                  text: t("report.Involves Multiple Locations"),
                  width: "*",
                  alignment: "right"
                }
              ]
            }
          : {
              text: maxPoint
                ? `${maxPoint.longitude}, ${maxPoint.latitude}`
                : "-"
            }
      ]);
    }

    // // 多点标记
    // if (maxValuePoints.length > 1) {
    //   extremeBody.push([
    //     { text: t("report.Involves Multiple Locations"), opacity: 0.6 },
    //     {
    //       columnGap: 2,
    //       columns: [
    //         {
    //           svg: svg.lineExtremeMultipleLocations,
    //           fit: [10, 10] as [number, number],
    //           width: 10,
    //           margin: [0, 0, 4, 0] as [number, number, number, number]
    //         },
    //         {
    //           text: `(${maxValuePoints.length} ${t("report.points")})`
    //         }
    //       ]
    //     }
    //   ]);
    // }

    content.push({
      text: t("report.Extreme Value and Location"),
      fontSize: 9,
      bold: true,
      margin: [0, 10, 0, 5]
    });
    content.push({
      table: {
        headerRows: 0,
        widths: ["*", "*"],
        body: extremeBody
      },
      layout: layout(),
      fontSize: 8,
      margin: [0, 0, 0, 10]
    });
  }

  // 5. 分位数指标小节（对齐前端展示文案与数值格式）
  if (metrics) {
    const percentileBody: any[][] = [];
    const p90Value = shouldAlignSummaryWithPage
      ? getMetricDisplayValueLikePage(t, metrics.p90)
      : formatMetricNumber(metrics.p90);
    const medianValue = shouldAlignSummaryWithPage
      ? getMetricDisplayValueLikePage(t, metrics.median)
      : formatMetricNumber(metrics.median);
    const meanValue = shouldAlignSummaryWithPage
      ? getMetricDisplayValueLikePage(t, summaryMetrics?.mean)
      : formatMetricNumber(metrics.mean);

    percentileBody.push([
      {
        text: t(`drawShape.polygonResultP90.${layerStatistic.layerId}`),
        opacity: 0.6
      },
      { text: p90Value }
    ]);

    percentileBody.push([
      {
        text: t(`drawShape.polygonResultMedian.${layerStatistic.layerId}`),
        opacity: 0.6
      },
      { text: medianValue }
    ]);

    if (layerStatistic.layerId !== "EQ_Landslide_Global_SwissRe") {
      percentileBody.push([
        {
          text: t(`drawShape.polygonResultMean.${layerStatistic.layerId}`),
          opacity: 0.6
        },
        { text: meanValue }
      ]);
    }

    if (percentileBody.length > 0) {
      content.push({
        text: t(`drawShape.Statistical Summary`),
        fontSize: 9,
        bold: true,
        margin: [0, 10, 0, 5]
      });
      content.push({
        table: {
          headerRows: 0,
          widths: ["*", "*"],
          body: percentileBody
        },
        layout: layout(),
        fontSize: 8,
        margin: [0, 0, 0, 10]
      });
    }
  }

  // 6. 区域风险分布小节
  if (
    layerStatistic.riskDistribution &&
    layerStatistic.riskDistribution.length > 0
  ) {
    const distributionBody: any[][] = [];
    const sortedRiskDistribution = sortRiskDistributionByLegend(
      layerStatistic.riskDistribution,
      legendInfo
    );

    for (const dist of sortedRiskDistribution) {
      const ratio = (toFiniteNumber(dist.ratio, 0) * 100).toFixed(1);
      distributionBody.push([
        getRiskLevelLegendSquare(String(dist.riskLevel || ""), legendInfo),
        {
          text: getTranslatedRiskLevel(t, String(dist.riskLevel || "")),
          fontSize: 8
        },
        { text: `${ratio}%`, fontSize: 8, alignment: "left" }
      ]);
    }

    content.push({
      text: t("report.Area Distribution"),
      fontSize: 9,
      bold: true,
      margin: [0, 10, 0, 5]
    });
    content.push({
      table: {
        headerRows: 1,
        widths: [12, 250, "*"],
        body: distributionBody
      },
      layout: layout(),
      fontSize: 8,
      margin: [0, 0, 0, 10]
    });
  }

  // 仅在非最后一项时换页，避免文档末尾生成空白页
  // if (!isLast) {
  //   content.push({ text: "", pageBreak: "before" });
  // }

  return {
    stack: content,
    margin: [0, 10, 0, 20]
  };
};

export default getHazardDetail;
